import { Component, computed, inject, Input, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocalizationModule, LocalizationService } from '@abp/ng.core';
import { ColDef, GridApi, IRowNode, RowDataUpdatedEvent } from '@ag-grid-community/core';
import { FieldTypes } from '@digiwall/ag-grid';
import {
  CreateCustomerPreparationFromDeliveryNotesResultDto,
  CustomerPreparationGroupDto,
  CustomerPreparationService,
  DeliveryNoteLinePreviewDto,
  EligibleDeliveryNoteForPreparationDto,
} from '@digiwall/goliath-documents/proxy';
import { GroupRowCellRendererComponent } from '@digiwall/goliath-core';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { Confirmation, ConfirmationService, DEFAULT_ERROR_LOCALIZATIONS } from '@abp/ng.theme.shared';
import { DocRole } from '@digiwall/goliath-core/proxy';
import { LinesEditorComponent, PreparationNavigationService } from '../../../../components';
import { EligibleDeliveryNotesGridComponent } from './eligible-delivery-notes-grid/eligible-delivery-notes-grid.component';
import { map, of, Subject, switchMap } from 'rxjs';

/**
 * Flattened tree row of the preview grid: one group row per preparation that will be generated,
 * with the delivery note lines feeding it beneath.
 */
type PreparationPreviewRow = Partial<DeliveryNoteLinePreviewDto> & {
  id: string;
  lineFromId: string | null;
  isGroupRow: boolean;
  displayReference: string;
};

@Component({
  selector: 'document-preparation-from-delivery-notes-dialog',
  templateUrl: './preparation-from-delivery-notes-dialog.component.html',
  imports: [
    DialogBoxComponent,
    LinesEditorComponent,
    LocalizationModule,
    EligibleDeliveryNotesGridComponent,
  ],
})
export class PreparationFromDeliveryNotesDialogComponent implements OnInit {
  // Assigned imperatively by the caller opening the offcanvas, so it stays a decorator input.
  @Input() deliveryNoteId!: string;

  /** `null` until the fetch resolves, so the "nothing to prepare" state is never flashed. */
  protected readonly eligibleDeliveryNotes = signal<EligibleDeliveryNoteForPreparationDto[] | null>(
    null,
  );
  /** Nothing eligible means nothing to preview: the line grid is left out of the dialog entirely. */
  protected readonly hasEligibleDeliveryNotes = computed(
    () => (this.eligibleDeliveryNotes()?.length ?? 0) > 0,
  );
  protected linesToPreview: PreparationPreviewRow[] = [];

  private readonly linesEditor = viewChild(LinesEditorComponent);
  private isPreviewGridConfigured = false;
  /** Checked line rows: the lines that will actually be prepared. */
  private readonly selectedLines = signal<PreparationPreviewRow[]>([]);
  /** Line rows already checked once, so a preview rebuild never re-checks what the user unchecked. */
  private readonly defaultedLineIds = new Set<string>();

  private selectedDeliveryNotes: EligibleDeliveryNoteForPreparationDto[] = [];
  private isSaving = false;

  /** Lines of a delivery note, kept so (de)selecting one again costs no round-trip. */
  private readonly deliveryNoteLinesCache = new Map<string, DeliveryNoteLinePreviewDto[]>();
  private readonly notesToFetch$ = new Subject<EligibleDeliveryNoteForPreparationDto[]>();

  private readonly localizationService = inject(LocalizationService);
  private readonly activeCanvas = inject(NgbActiveOffcanvas);
  private readonly customerPreparationService = inject(CustomerPreparationService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly preparationNavigation = inject(PreparationNavigationService);

  constructor() {
    this.notesToFetch$
      .pipe(
        // switchMap: a selection change that arrives while a fetch is in flight cancels it. The
        // superseded notes are still uncached, so the next emission asks for them again.
        switchMap(notes => {
          const noteIds = notes.map(note => note.id!).filter(id => !!id);

          return noteIds.length
            ? this.customerPreparationService
                .getDeliveryNoteLinesToPrepare(noteIds)
                .pipe(map(lines => ({ noteIds, lines: lines ?? [] })))
            : of({ noteIds, lines: [] as DeliveryNoteLinePreviewDto[] });
        }),
        takeUntilDestroyed(),
      )
      .subscribe(({ noteIds, lines }) => {
        // Seed every requested note first: one that yields no line still counts as fetched, so
        // selecting it again costs no round-trip.
        noteIds.forEach(noteId => this.deliveryNoteLinesCache.set(noteId, []));
        lines.forEach(line => this.deliveryNoteLinesCache.get(line.deliveryNoteId!)?.push(line));
        this.rebuildPreview();
      });
  }

  ngOnInit(): void {
    this.customerPreparationService
      .getEligibleDeliveryNotes(this.deliveryNoteId)
      .subscribeOnce(notes => this.eligibleDeliveryNotes.set(notes ?? []));
  }

  protected readonly getContextMenuItems = _ => [];

  protected readonly getDataPath = (row: PreparationPreviewRow): string[] =>
    row.isGroupRow ? [row.id] : [row.lineFromId!, row.id];

  // Group rows carry the chapter look of the document line editors. Every other row is a
  // read-only preview line, so the inherited 'ag-row-disabled' grey is dropped: it would make
  // the whole grid look disabled.
  protected readonly rowClassRules = {
    'chapter-line': params => params.data?.isGroupRow === true,
    'ag-row-disabled': () => false,
  };

  protected columnDefs: ColDef[] = [
    {
      field: 'lineFromIdRow',
      headerName: '▼',
      showRowGroup: 'lineFromId',
      cellRenderer: GroupRowCellRendererComponent,
      suppressHeaderMenuButton: true,
      pinned: 'left',
      suppressMovable: true,
      width: 60,
      lockVisible: true,
    },
    {
      field: 'displayReference',
      headerName: '#',
      type: FieldTypes.String,
      width: 70,
      pinned: 'left',
    },
    {
      field: 'deliveryNoteDisplayNumber',
      headerName: this.localizationService.instant('Doc::CustomerDeliveryNote:DisplayNumber'),
      type: FieldTypes.String,
      width: 120,
      pinned: 'left',
    },
    {
      field: 'code',
      headerName: this.localizationService.instant('Doc::CustomerReference'),
      type: FieldTypes.String,
      width: 150,
    },
    {
      field: 'description',
      headerName: this.localizationService.instant('::Description'),
      type: FieldTypes.String,
      flex: 1,
    },
    {
      field: 'articleSalesUnitCode',
      headerName: this.localizationService.instant('Product::Article:ProductSaleUnit'),
      type: FieldTypes.String,
      width: 80,
    },
    {
      field: 'quantity',
      headerName: this.localizationService.instant('::Quantity'),
      type: FieldTypes.Number,
      width: 110,
    },
    {
      field: 'preparedQuantity',
      headerName: this.localizationService.instant('Doc::Preparation:AlreadyPreparedQuantity'),
      type: FieldTypes.Number,
      width: 130,
    },
    {
      field: 'remainingQuantity',
      headerName: this.localizationService.instant('Doc::Preparation:RemainingQuantity'),
      type: FieldTypes.Number,
      width: 130,
    },
    {
      field: 'stockLocation',
      headerName: this.localizationService.instant('GoliathCore::Company:StorageLocation'),
      type: FieldTypes.String,
      width: 130,
    },
    {
      field: 'articleRangeValue1',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleRangeValue1'),
      type: FieldTypes.String,
      width: 120,
    },
    {
      field: 'articleRangeValue2',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleRangeValue2'),
      type: FieldTypes.String,
      width: 120,
    },
    {
      field: 'articleLot',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleLot'),
      type: FieldTypes.String,
      width: 120,
    },
    {
      field: 'articleSerial',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleSerial'),
      type: FieldTypes.String,
      width: 130,
    },
  ];

  protected onDeliveryNoteSelectionChange(selected: EligibleDeliveryNoteForPreparationDto[]): void {
    const uniqueNotes = new Map<string, EligibleDeliveryNoteForPreparationDto>();
    (selected ?? []).forEach(note => note?.id && uniqueNotes.set(note.id, note));

    this.selectedDeliveryNotes = [...uniqueNotes.values()].sort((left, right) =>
      (left.displayNumber ?? '').localeCompare(right.displayNumber ?? ''),
    );

    this.rebuildPreview();
    this.notesToFetch$.next(
      this.selectedDeliveryNotes.filter(note => !this.deliveryNoteLinesCache.has(note.id!)),
    );
  }

  protected get dialogBoxTitle(): string {
    return this.localizationService.instant('Doc::CustomerSalesPurchase:GeneratePreparation');
  }

  protected get canSave(): boolean {
    return !this.isSaving && this.selectedLines().length > 0;
  }

  protected save(): void {
    if (!this.canSave) return;

    const lines = this.selectedLines();
    const selectedLineIds = lines.map(line => line.id);
    // Derived from the checked lines, not from the checked notes: a note whose every line was
    // unchecked stays out of the mutex set and out of the impacted-notes count.
    const deliveryNoteIds = [
      ...new Set(lines.map(line => line.deliveryNoteId).filter((id): id is string => !!id)),
    ];

    this.isSaving = true;
    this.customerPreparationService
      .createFromDeliveryNotes({ deliveryNoteIds, selectedLineIds })
      .subscribeOnce(
        result => {
          this.isSaving = false;
          this.showResultSummary(result);
          this.activeCanvas.close(result);
        },
        error => {
          this.isSaving = false;
          this.showServerError(error);
        },
      );
  }

  /**
   * Configures the preview grid on the first rebuild instead of from `ngAfterViewInit`: the editor is
   * only rendered once eligible notes came back, so the view-init hook fires before the grid exists.
   * A rebuild always precedes the row assignment that renders them, so the options land in time.
   */
  private configurePreviewGrid(): void {
    if (this.isPreviewGridConfigured) return;

    const api = this.linesEditor()?.grid?.grid?.api;
    if (api == null) return;

    this.isPreviewGridConfigured = true;

    api.setGridOption('rowSelection', {
      mode: 'multiRow',
      // Unchecking a preparation group row unchecks the lines feeding it.
      groupSelects: 'descendants',
      isRowSelectable: params => !!params.data,
      enableSelectionWithoutKeys: true,
      hideDisabledCheckboxes: false,
    });

    api.setGridOption('onRowDataUpdated', (event: RowDataUpdatedEvent<PreparationPreviewRow>) =>
      this.selectNewLinesByDefault(event.api),
    );

    api.setGridOption('onRowSelected', () => this.refreshSelectedLines(api));
  }

  /**
   * Checks every line the user has not seen yet, so selecting a delivery note prepares all of it by
   * default. Rows already offered once are left alone: the preview is rebuilt on every note
   * (de)selection, and row ids are stable, so re-checking them would undo the user's own unchecks.
   */
  private selectNewLinesByDefault(api: GridApi): void {
    const nodesToSelect: IRowNode[] = [];

    api.forEachNode((node: IRowNode<PreparationPreviewRow>) => {
      const row = node.data;
      if (row == null || row.isGroupRow || this.defaultedLineIds.has(row.id)) return;

      this.defaultedLineIds.add(row.id);
      nodesToSelect.push(node);
    });

    if (nodesToSelect.length) {
      api.setNodesSelected({ nodes: nodesToSelect, newValue: true });
    }

    this.refreshSelectedLines(api);
  }

  /**
   * Read from the grid api rather than from AgGridComponent's `selectedRows` model: that model is
   * maintained by toggling on every row-selected event, so a programmatic (re)selection would push
   * the same row twice.
   */
  private refreshSelectedLines(api: GridApi): void {
    this.selectedLines.set(
      (api.getSelectedRows() as PreparationPreviewRow[]).filter(row => !row.isGroupRow),
    );
  }

  /**
   * Rebuilds the whole preview from the cache instead of appending to it: the rows are a pure
   * function of the current selection, so repeated or out-of-order selection events cannot
   * duplicate lines.
   */
  private rebuildPreview(): void {
    this.configurePreviewGrid();

    const groups = new Map<
      string,
      { group?: CustomerPreparationGroupDto; lines: DeliveryNoteLinePreviewDto[] }
    >();

    for (const note of this.selectedDeliveryNotes) {
      for (const line of this.deliveryNoteLinesCache.get(note.id!) ?? []) {
        const key = line.group?.key ?? '';
        const group = groups.get(key) ?? { group: line.group, lines: [] };
        group.lines.push(line);
        groups.set(key, group);
      }
    }

    const rows: PreparationPreviewRow[] = [];
    [...groups.entries()]
      .sort(([, left], [, right]) =>
        this.buildGroupLabel(left.group).localeCompare(this.buildGroupLabel(right.group)),
      )
      .forEach(([key, { group, lines }], groupIndex) => {
        const groupRowId = `group:${key}`;
        const groupReference = (groupIndex + 1).toString();

        rows.push({
          id: groupRowId,
          lineFromId: null,
          isGroupRow: true,
          displayReference: groupReference,
          description: this.buildGroupLabel(group),
        });

        lines.forEach((line, lineIndex) =>
          rows.push({
            ...line,
            id: line.id!,
            lineFromId: groupRowId,
            isGroupRow: false,
            displayReference: `${groupReference}.${lineIndex + 1}`,
          }),
        );
      });

    // Forget lines that left the preview: their delivery note was deselected, so if it comes back
    // its lines are offered checked again instead of silently staying unchecked.
    const previewLineIds = new Set(rows.filter(row => !row.isGroupRow).map(row => row.id));
    [...this.defaultedLineIds]
      .filter(id => !previewLineIds.has(id))
      .forEach(id => this.defaultedLineIds.delete(id));

    this.linesToPreview = rows;
  }

  /** Logistic grouping the backend generates on: customer, delivery address, stock location site. */
  private buildGroupLabel(group?: CustomerPreparationGroupDto): string {
    return [group?.customer, group?.deliveryAddress, group?.companySite]
      .map(part => part?.trim())
      .filter(part => !!part)
      .join(' - ');
  }

  private showResultSummary(result: CreateCustomerPreparationFromDeliveryNotesResultDto): void {
    const successMessage = this.localizationService.instant(
      'Doc::CustomerPreparation:GenerationSummaryFromDeliveryNote',
      result.createdPreparationsCount.toString(),
      result.createdLinesCount.toString(),
      result.impactedDeliveryNotesCount.toString(),
    );

    const dialogLines: string[] = [successMessage];

    if ((result.errors?.length ?? 0) > 0) {
      const errorMessage = this.localizationService.instant(
        'Doc::CustomerPreparation:GenerationErrorsCount',
        result.errors.length.toString(),
      );
      const detailedMessages = result.errors
        .map(e => e.message?.trim())
        .filter((m): m is string => !!m);

      dialogLines.push('', errorMessage);
      if (detailedMessages.length > 0) {
        dialogLines.push(...[...new Set(detailedMessages)].map(m => `- ${m}`));
      }
    }

    const hasErrors = (result.errors?.length ?? 0) > 0;
    const title = {
      key: hasErrors ? '::Warning' : '::Information',
      defaultValue: hasErrors ? 'Warning' : 'Information',
    };
    const message = { key: dialogLines.join('\n'), defaultValue: dialogLines.join('\n') };

    // Anything generated turns the summary into a way in: confirming opens the new preparation,
    // asking which one when the generation produced several.
    const canOpen = this.preparationNavigation.hasGenerated(result.createdPreparations);
    const options = canOpen
      ? {
          yesText: 'Doc::CustomerPreparation:OpenGeneratedPreparation',
          cancelText: 'AbpAccount::Close',
        }
      : { hideCancelBtn: true, yesText: 'AbpAccount::Close' };

    const summary = hasErrors
      ? this.confirmationService.warn(message, title, options)
      : this.confirmationService.info(message, title, options);

    if (!canOpen) return;

    summary.subscribeOnce(status => {
      if (status === Confirmation.Status.confirm) {
        this.preparationNavigation.openGenerated(
          result.createdPreparations,
          DocRole.CustomerPreparation,
        );
      }
    });
  }

  private showServerError(error: unknown): void {
    const remoteError = (error as any)?.error?.error;
    const remoteCode = remoteError?.code as string | undefined;
    const remoteMessage = remoteError?.message as string | undefined;

    if (remoteCode) {
      const localized = this.localizationService.instant(remoteCode);
      if (localized && localized !== remoteCode) {
        this.confirmationService.error(
          { key: localized, defaultValue: localized },
          {
            key: DEFAULT_ERROR_LOCALIZATIONS.defaultError.title,
            defaultValue: DEFAULT_ERROR_LOCALIZATIONS.defaultError.title,
          },
          { hideCancelBtn: true, yesText: 'AbpAccount::Close' },
        );
        return;
      }
    }

    const fallback =
      remoteMessage?.trim() || this.localizationService.instant('AbpUi::DefaultErrorMessage');
    this.confirmationService.error(
      { key: fallback, defaultValue: fallback },
      {
        key: DEFAULT_ERROR_LOCALIZATIONS.defaultError.title,
        defaultValue: DEFAULT_ERROR_LOCALIZATIONS.defaultError.title,
      },
      { hideCancelBtn: true, yesText: 'AbpAccount::Close' },
    );
  }
}
